import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { ActionName } from '../../../../../constants/Bi';
import { ContextMenuId } from '../../../../../constants/ContextMenuId';
import { HELP_LINKS } from '../../../../../constants/HelpLinks';
import { ModalName } from '../../../../../constants/ModalName';
import { MainSource } from '../../../../../constants/Source';
import { ActionClickOrigin } from '../../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import {
  TrackBoardActionsMenuClickOrigin,
  TrackBoardActionsMenuTarget,
  TrackBoardClickOrigin,
  useTrackBoardAnalytics,
} from '../../../../../utils/hooks/bi/useTrackBoardAnalytics';
import { useTrackInfoPanelFileActions } from '../../../../../utils/hooks/bi/useTrackInfoPanelFileActions';
import { ClickOrigin as ClickOriginLearnMore } from '../../../../../utils/hooks/bi/useTrackLearnMoreClickAnalytics';
import { ClickOrigin } from '../../../../../utils/hooks/bi/useTrackTrashActions';
import { useTrackTrashButtons } from '../../../../../utils/hooks/bi/useTrackTrashButtons';
import { useModalHandlersAndOptions } from '../../../../../utils/hooks/useModal';
import {
  usePermanentDeleteModalHandlers,
  useShouldShowPermanentDelete,
} from '../../../../../utils/hooks/useModal/modals/usePermanentDeleteModal';
import { useSources } from '../../../../../utils/hooks/useSources';
import { MediaManagerSDKBoard } from '../../../../../utils/media-manager-sdk';
import { isBoard, isFavoritesBoard } from '../../../../../utils/media-manager-sdk/boards';
import { isRootFolder, validateFileName } from '../../../../../utils/media-manager-sdk/items';
import { HelpLink, Text, WixIcons } from '../../../../../components/common';
import { ActionItem } from '../ActionItem';
import { Actions } from '../Actions';
import { BoardInfo } from '../BoardInfo';
import { DeleteFlowPanelInformation } from '../DeleteFlowPanelInformation';
import { EditingItemName } from '../EditingItemName';
import { ItemInfoTable } from '../ItemInfoTable';
import styles from './NoItemsBottomPart.scss';
import { IOuter } from './NoItemsBottomPart.types';
import { NavItemTitle } from '../NavItemTitle';
import { useFileAwarenessModalHandlers } from '../../../../../utils/hooks/useModal/modals/useFileAwarenessModal';
import { useMMGRSettings } from '../../../../../utils/hoc/withMMGRSettings';

const translationPropsMap = {
  [MainSource.SiteFiles]: {
    id: 'panel.bottom-parts.no-items-bottom-part.site-files-empty',
    values: {
      contributorsLink: (
        <HelpLink article={HELP_LINKS.CONTRIBUTORS} size="small">
          <FormattedMessage id="panel.bottom-parts.no-items-bottom-part.site-files-empty.contributors" />
        </HelpLink>
      ),
    },
  },
  [MainSource.Trash]: { id: 'panel.bottom-parts.no-items-bottom-part.trash-empty' },
  [MainSource.Favorites]: { id: 'panel.bottom-parts.no-items-bottom-part.favorites-empty' },
  [MainSource.Recommended]: { id: 'panel.bottom-parts.no-items-bottom-part.recommended-empty' },
  [MainSource.MediaFromWix]: { id: 'panel.bottom-parts.no-items-bottom-part.free-from-wix-empty' },
  [MainSource.Shutterstock]: { id: 'panel.bottom-parts.no-items-bottom-part.shutterstock-empty' },
  [MainSource.Boards]: { id: 'panel.bottom-parts.no-items-bottom-part.boards-empty' },
};

export const NoItemsBottomPart: FC<IOuter> = ({
  currentFolder,
  source,
  updateItem,
  updateFolderInPath,
  createNewFolderStartFromTop,
  onRemoveItemsFromBoard,
}) => {
  const { isTrash, isFavorites, isBoards, isBoardsRoot } = useSources({ source });
  const { mmgrSettings } = useMMGRSettings();

  const { isFileAwarenessEnabled, disableTeams } = mmgrSettings;

  const translationProps = translationPropsMap[source] || null;

  const onRenameComplete = useCallback(
    (editingName) => {
      const validFileName = validateFileName(editingName);
      if (validFileName && validFileName !== currentFolder.name) {
        updateFolderInPath(currentFolder.id, { name: validFileName });
        updateItem(currentFolder, { name: validFileName });
      }
    },
    [currentFolder, updateFolderInPath, updateItem],
  );

  const canDisplayCreateNewFolderButton = !isTrash && !isFavorites && !isBoards;

  const { trackEmptyTrashButtonClicked } = useTrackTrashButtons();

  const { openModal: openRemoveFilesModal } = usePermanentDeleteModalHandlers();
  const { openModal: openFileAwarenessModal } = useFileAwarenessModalHandlers();

  const { trackCreateBoardClick } = useTrackBoardAnalytics();

  const { trackInfoPanelFileAction } = useTrackInfoPanelFileActions();

  const handleEmptyTrashButtonClick = useCallback(() => {
    if (isFileAwarenessEnabled) {
      openFileAwarenessModal({ clickOrigin: ClickOrigin.MoreInfo, selectedItems: [] });
    } else {
      openRemoveFilesModal({ clickOrigin: ClickOrigin.MoreInfo });
    }
    trackInfoPanelFileAction(currentFolder, ActionName.EmptyTrash);
    trackEmptyTrashButtonClicked();
  }, [
    trackInfoPanelFileAction,
    currentFolder,
    trackEmptyTrashButtonClicked,
    isFileAwarenessEnabled,
    openFileAwarenessModal,
    openRemoveFilesModal,
  ]);

  const handleCreateFolderClick = useCallback(() => {
    trackInfoPanelFileAction(currentFolder, ActionName.CreateFolder);
    createNewFolderStartFromTop();
  }, [trackInfoPanelFileAction, currentFolder, createNewFolderStartFromTop]);

  const { openModal: openEditBoardModal } = useModalHandlersAndOptions(ModalName.EditBoard);
  const { openModal: openAddFilesToBoardModal } = useModalHandlersAndOptions(ModalName.AddFilesToBoard);

  const { trackBoardActionsClick } = useTrackBoardAnalytics();
  const handleAddFilesToBoardClick = useCallback(() => {
    openAddFilesToBoardModal({ boards: [currentFolder], shouldUpdateItemsInFolder: true });

    trackBoardActionsClick([currentFolder] as MediaManagerSDKBoard[], {
      target: TrackBoardActionsMenuTarget.AddFiles,
      clickOrigin: TrackBoardActionsMenuClickOrigin.InfoPanel,
    });
  }, [currentFolder, openAddFilesToBoardModal, trackBoardActionsClick]);

  const handleEditBoardClick = useCallback(() => {
    openEditBoardModal({ board: currentFolder, clickOrigin: ActionClickOrigin.InfoPanel });

    trackBoardActionsClick([currentFolder] as MediaManagerSDKBoard[], {
      target: TrackBoardActionsMenuTarget.Edit,
      clickOrigin: TrackBoardActionsMenuClickOrigin.InfoPanel,
    });
  }, [currentFolder, openEditBoardModal, trackBoardActionsClick]);

  const handleCreateBoardClick = useCallback(() => {
    openEditBoardModal({ clickOrigin: ActionClickOrigin.InfoPanel });
    trackCreateBoardClick(TrackBoardClickOrigin.InfoPanel);
  }, [openEditBoardModal, trackCreateBoardClick]);

  const isInsideBoard = isBoard(currentFolder) && !isBoardsRoot;

  const shouldShowPermanentDeleteInfo = useShouldShowPermanentDelete(source);

  const shouldShowInformation = !shouldShowPermanentDeleteInfo && !isInsideBoard && !disableTeams;

  return (
    <div data-hook="no-items-bottom-part">
      {isBoard(currentFolder) ? (
        <BoardInfo name={currentFolder.name} description={currentFolder.description} />
      ) : (
        <EditingItemName item={currentFolder} onRenameComplete={onRenameComplete} />
      )}

      {!isFavorites && (
        <>
          <Actions
            className={styles.actions}
            dataHook="actions"
            currentFolder={currentFolder}
            isMoreActionsVisible={!isRootFolder(currentFolder)}
            source={source}
            isInsideBoard={isInsideBoard}
            contextMenuId={ContextMenuId.CurrentFolder}
            onRemoveItemsFromBoard={onRemoveItemsFromBoard}
          >
            {isTrash && (
              <ActionItem
                className={styles.actionItem}
                icon={<WixIcons.EmptyTrash />}
                label={<FormattedMessage id="actions.empty-trash" />}
                onClick={handleEmptyTrashButtonClick}
                dataHook="actions.empty-trash-button"
              />
            )}

            {canDisplayCreateNewFolderButton && (
              <ActionItem
                className={styles.actionItem}
                icon={<WixIcons.NewFolder />}
                label={<FormattedMessage id="actions.create-new-folder" />}
                onClick={handleCreateFolderClick}
                dataHook="actions.create-new-folder-button"
              />
            )}

            {isBoardsRoot && (
              <ActionItem
                className={styles.actionItem}
                icon={<WixIcons.Add />}
                label={<FormattedMessage id="actions.create-new-board" />}
                onClick={handleCreateBoardClick}
                dataHook="actions.create-new-board"
              />
            )}

            {isInsideBoard && (
              <>
                <ActionItem
                  className={styles.actionItem}
                  icon={<WixIcons.Add />}
                  label={<FormattedMessage id="actions.add-files-to-board" />}
                  onClick={handleAddFilesToBoardClick}
                  dataHook="actions.add-files-to-board"
                />

                {!isFavoritesBoard(currentFolder as MediaManagerSDKBoard) && (
                  <ActionItem
                    className={styles.actionItem}
                    icon={<WixIcons.Edit />}
                    label={<FormattedMessage id="actions.edit-board" />}
                    onClick={handleEditBoardClick}
                    dataHook="actions.edit-board"
                  />
                )}
              </>
            )}
          </Actions>
        </>
      )}

      {translationProps && shouldShowInformation && (
        <>
          <NavItemTitle className={styles.info}>
            <FormattedMessage id="panel.bottom-parts.no-items-bottom-part.information" />
          </NavItemTitle>
          <Text weight="thin" size="small" secondary tagName="div" className={styles.infoContent}>
            <FormattedMessage {...translationProps} />
          </Text>
        </>
      )}

      {isInsideBoard && (
        <>
          <NavItemTitle className={styles.boardInfo}>
            <FormattedMessage id="panel.bottom-parts.no-items-bottom-part.information" />
          </NavItemTitle>
          <ItemInfoTable item={currentFolder} source={source} />
        </>
      )}

      {shouldShowPermanentDeleteInfo && <DeleteFlowPanelInformation clickOrigin={ClickOriginLearnMore.Trash} />}
    </div>
  );
};
